import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../App';
import { apiFetch } from '../lib/api';

const TABS_SUPERADMIN = ['Çalışmalar', 'Kullanıcılar'];
const TABS_ADMIN = ['Çalışmalar'];

const DRAFT_KEY = 'tt_trial_draft';
const DRAFT_CRITERIA_KEY = 'tt_trial_criteria';
const DRAFT_ARMS_KEY = 'tt_trial_arms';
const DRAFT_INVESTIGATORS_KEY = 'tt_trial_investigators';
const EMPTY_FORM = { title: '', short_code: '', phase: '', branch_id: '', disease_id: '', is_randomized: false, status: 'open', start_date: '', estimated_end_date: '', nct_number: '', notes: '' };
const EMPTY_ARM = { name: '', agents: '', description: '' };
const EMPTY_INVESTIGATOR = { center_name: '', city: '', investigator: '', phone: '', email: '' };

function ls_get(key, fallback) {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; }
  catch(e) { return fallback; }
}
function ls_set(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); } catch(e) {} }
function ls_del(...keys) { keys.forEach(k => { try { localStorage.removeItem(k); } catch(e) {} }); }
function hasDraft() {
  const d = ls_get(DRAFT_KEY, {});
  return !!(d.title || d.nct_number);
}

export default function AdminPanel() {
  const { profile } = useAuth();
  const isSuperAdmin = profile?.role === 'superadmin';
  const TABS = isSuperAdmin ? TABS_SUPERADMIN : TABS_ADMIN;

  const [activeTab, setActiveTab] = useState('Çalışmalar');
  const [trials, setTrials] = useState([]);
  const [diseases, setDiseases] = useState([]);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTrialForm, setShowTrialForm] = useState(hasDraft);
  const [editingTrial, setEditingTrial] = useState(null);
  const [message, setMessage] = useState('');

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    const queries = [
      supabase.from('trials').select('*, branches(name), trial_diseases(disease_id, diseases(id,name,code))').order('created_at', { ascending: false }),
      supabase.from('diseases').select('*').order('name'),
      supabase.from('branches').select('*').order('id'),
    ];
    if (isSuperAdmin) queries.push(supabase.from('profiles').select('*').order('created_at', { ascending: false }));
    const [t, d, b, u] = await Promise.all(queries);
    setTrials(t.data || []);
    setDiseases(d.data || []);
    setBranches(b.data || []);
    if (isSuperAdmin) setUsers(u?.data || []);
    setLoading(false);
  }, [isSuperAdmin]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  function showMsg(msg) { setMessage(msg); setTimeout(() => setMessage(''), 3000); }
  function canEdit(trial) { return isSuperAdmin || trial.created_by === profile?.id; }

  async function deleteTrial(id) {
    if (!window.confirm('Bu çalışmayı silmek istediğinizden emin misiniz?')) return;
    await supabase.from('trials').delete().eq('id', id);
    showMsg('Çalışma silindi.'); fetchAll();
  }
  async function toggleApprove(user) {
    await supabase.from('profiles').update({ approved: !user.approved }).eq('id', user.id);
    showMsg(user.approved ? 'Askıya alındı.' : 'Onaylandı.'); fetchAll();
  }
  async function deleteUser(user) {
    if (!window.confirm(user.full_name + ' kullanıcısını silmek istediğinizden emin misiniz?')) return;
    try {
      const r = await apiFetch('/api/delete-user', { method: 'POST', body: { userId: user.id } });
      if (!r.ok) {
        const err = await r.json();
        throw new Error(err.error || 'Silme hatası');
      }
      showMsg('Kullanıcı silindi.');
      fetchAll();
    } catch(e) {
      alert('Silme hatası: ' + e.message);
    }
  }

  async function setUserRole(user, newRole) {
    const { error } = await supabase.from('profiles').update({ role: newRole, requested_role: null }).eq('id', user.id);
    showMsg(error ? 'Hata: ' + error.message : 'Rol güncellendi.'); fetchAll();
  }
  async function rejectRoleRequest(user) {
    const { error } = await supabase.from('profiles').update({ requested_role: null }).eq('id', user.id);
    showMsg(error ? 'Hata: ' + error.message : 'Talep reddedildi.'); fetchAll();
  }

  const pendingRequests = users.filter(u => u.requested_role);

  if (loading) return <div style={S.center}>Yükleniyor...</div>;

  return (
    <div style={S.container}>
      <div style={S.pageHeader}>
        <h2 style={S.pageTitle}>⚙️ Yönetim Paneli</h2>
        <span style={{ ...S.myRoleBadge, background: isSuperAdmin ? '#f3e8ff' : '#dbeafe', color: isSuperAdmin ? '#7c3aed' : '#1d4ed8' }}>
          {isSuperAdmin ? '👑 Süper Admin' : '🏥 Merkez Sorumlusu'}
        </span>
      </div>
      {message && <div style={S.toast}>{message}</div>}

      <div style={S.tabBar}>
        {TABS.map(tab => (
          <button key={tab} style={{ ...S.tabBtn, ...(activeTab === tab ? S.tabActive : {}) }} onClick={() => setActiveTab(tab)}>
            {tab === 'Çalışmalar' && `📋 ${tab} (${trials.length})`}
            {tab === 'Kullanıcılar' && `👥 ${tab} (${users.length})${pendingRequests.length ? ` · ${pendingRequests.length} talep` : ''}`}
          </button>
        ))}
      </div>

      {activeTab === 'Çalışmalar' && (
        <div>
          <div style={S.secHeader}>
            <h3 style={S.secTitle}>Klinik Çalışmalar</h3>
            <button style={S.addBtn} onClick={() => { setEditingTrial(null); setShowTrialForm(true); }}>+ Yeni Çalışma</button>
          </div>
          {!isSuperAdmin && <div style={S.infoBox}>ℹ️ Yalnızca <strong>kendi kaydettiğiniz</strong> çalışmaları düzenleyebilir veya silebilirsiniz.</div>}
          {showTrialForm && (
            <TrialForm
              trial={editingTrial} diseases={diseases} branches={branches} currentUserId={profile?.id} onRefresh={fetchAll}
              onSave={() => { setShowTrialForm(false); setEditingTrial(null); fetchAll(); showMsg('Çalışma kaydedildi.'); }}
              onCancel={() => { setShowTrialForm(false); setEditingTrial(null); }}
            />
          )}
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr style={S.thead}>
                <th style={S.th}>Çalışma Adı</th><th style={S.th}>Faz</th>
                <th style={S.th}>Hastalık</th><th style={S.th}>Durum</th><th style={S.th}>İşlem</th>
              </tr></thead>
              <tbody>
                {trials.map((t, i) => (
                  <tr key={t.id} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc' }}>
                    <td style={S.td}><span style={{ fontWeight: 500 }}>{t.title}</span>{t.short_code && <span style={S.code}>{t.short_code}</span>}</td>
                    <td style={S.td}>{t.phase || '-'}</td>
                    <td style={S.td}>{(t.trial_diseases?.map(td => td.diseases?.code).filter(Boolean) || []).join(', ') || '-'}</td>
                    <td style={S.td}><span style={{ ...S.dot, background: t.status === 'open' ? '#10b981' : t.status === 'suspended' ? '#f59e0b' : '#ef4444' }}>{t.status === 'open' ? 'Açık' : t.status === 'suspended' ? 'Askıda' : 'Kapalı'}</span></td>
                    <td style={S.td}>
                      {canEdit(t) ? (<><button style={S.editBtn} onClick={() => { setEditingTrial(t); setShowTrialForm(true); }}>Düzenle</button><button style={S.delBtn} onClick={() => deleteTrial(t.id)}>Sil</button></>) : <span style={S.noEdit}>—</span>}
                    </td>
                  </tr>
                ))}
                {trials.length === 0 && <tr><td colSpan={5} style={S.emptyCell}>Henüz çalışma eklenmemiş.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'Kullanıcılar' && isSuperAdmin && (
        <div>
          <h3 style={S.secTitle}>Kullanıcı Yönetimi</h3>
          {pendingRequests.length > 0 && (
            <div style={S.requestBox}>
              <strong>⏳ Bekleyen Merkez Sorumlusu talepleri ({pendingRequests.length})</strong>
              {pendingRequests.map(u => (
                <div key={u.id} style={S.requestRow}>
                  <span><strong>{u.full_name || '-'}</strong> · {u.email} · {u.institution || 'kurum belirtilmemiş'}{!u.healthcare_confirmed && <span style={S.warnTag}>sağlık çalışanı onayı yok</span>}</span>
                  <span>
                    <button style={S.approveBtn} onClick={() => setUserRole(u, u.requested_role)}>Onayla</button>
                    <button style={{ ...S.delBtn, marginLeft: '0.4rem' }} onClick={() => rejectRoleRequest(u)}>Reddet</button>
                  </span>
                </div>
              ))}
            </div>
          )}
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr style={S.thead}>
                <th style={S.th}>Ad Soyad</th><th style={S.th}>E-posta</th>
                <th style={S.th}>Kurum</th><th style={S.th}>Sağlık Çalışanı</th><th style={S.th}>Rol</th>
                <th style={S.th}>Durum</th><th style={S.th}>İşlem</th>
              </tr></thead>
              <tbody>
                {users.map((u, i) => (
                  <tr key={u.id} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc' }}>
                    <td style={S.td}>{u.full_name || '-'}</td>
                    <td style={S.td}>{u.email}</td>
                    <td style={S.td}>{u.institution || '-'}</td>
                    <td style={S.td}>{u.healthcare_confirmed ? '✅' : '—'}</td>
                    <td style={S.td}>
                      <select style={S.roleSelect} value={u.role} onChange={e => setUserRole(u, e.target.value)} disabled={u.id === profile?.id}>
                        <option value="viewer">👁️ Kullanıcı</option>
                        <option value="admin">🏥 Merkez Sorumlusu</option>
                        <option value="superadmin">👑 Süper Admin</option>
                      </select>
                    </td>
                    <td style={S.td}><span style={{ ...S.dot, background: u.approved ? '#10b981' : '#f59e0b' }}>{u.approved ? 'Onaylı' : 'Bekliyor'}</span></td>
                    <td style={S.td}>
                      {u.id !== profile?.id ? (
                        <>
                          <button style={u.approved ? S.delBtn : S.approveBtn} onClick={() => toggleApprove(u)}>{u.approved ? 'Askıya Al' : 'Onayla'}</button>
                          <button style={{ ...S.delBtn, background: '#fee2e2', color: '#dc2626', marginTop: '0.25rem' }} onClick={() => deleteUser(u)}>Sil</button>
                        </>
                      ) : <span style={S.noEdit}>Sen</span>}
                    </td>
                  </tr>
                ))}
                {users.length === 0 && <tr><td colSpan={7} style={S.emptyCell}>Henüz kullanıcı yok.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Trial Form ───────────────────────────────────────────────────────────────
function TrialForm({ trial, diseases, branches, currentUserId, onSave, onCancel, onRefresh }) {
  const { profile } = useAuth();
  const isNew = !trial;
  const [form, setForm] = useState(() => isNew ? ls_get(DRAFT_KEY, EMPTY_FORM) : {
    title: trial.title || '', short_code: trial.short_code || '', phase: trial.phase || '',
    branch_id: trial.branch_id != null ? String(trial.branch_id) : '',
    disease_id: trial.disease_id || '',
    is_randomized: trial.is_randomized || false, status: trial.status || 'open',
    start_date: trial.start_date || '', estimated_end_date: trial.estimated_end_date || '',
    nct_number: trial.nct_number || '', notes: trial.notes || '',
  });
  const [criteria, setCriteria] = useState(() => isNew ? ls_get(DRAFT_CRITERIA_KEY, []) : []);
  const [arms, setArms] = useState(() => isNew ? ls_get(DRAFT_ARMS_KEY, []) : []);
  const [selectedDiseases, setSelectedDiseases] = useState(() => isNew ? ls_get('tt_trial_diseases', []) : []);
  const [investigators, setInvestigators] = useState(() => isNew ? ls_get(DRAFT_INVESTIGATORS_KEY, []) : []);
  const [newC, setNewC] = useState({ type: 'inclusion', description: '' });
  const [saving, setSaving] = useState(false);
  const [sendNotification, setSendNotification] = useState(false);
  const [notifyMsg, setNotifyMsg] = useState('');
  const [showAddBranch, setShowAddBranch] = useState(false);
  const [newBranchName, setNewBranchName] = useState('');
  const [showAddDisease, setShowAddDisease] = useState(false);
  const [newDiseaseName, setNewDiseaseName] = useState('');
  const [newDiseaseCode, setNewDiseaseCode] = useState('');

  useEffect(() => { if (isNew) ls_set(DRAFT_KEY, form); }, [form, isNew]);
  useEffect(() => { if (isNew) ls_set(DRAFT_CRITERIA_KEY, criteria); }, [criteria, isNew]);
  useEffect(() => { if (isNew) ls_set(DRAFT_ARMS_KEY, arms); }, [arms, isNew]);
  useEffect(() => { if (isNew) ls_set('tt_trial_diseases', selectedDiseases); }, [selectedDiseases, isNew]);
  useEffect(() => { if (isNew) ls_set(DRAFT_INVESTIGATORS_KEY, investigators); }, [investigators, isNew]);

  useEffect(() => {
    if (trial?.id) {
      supabase.from('criteria').select('*').eq('trial_id', trial.id).order('order_index').then(({ data }) => setCriteria(data || []));
      supabase.from('trial_arms').select('*').eq('trial_id', trial.id).order('order_index').then(({ data }) => setArms(data || []));
      supabase.from('trial_diseases').select('disease_id').eq('trial_id', trial.id).then(({ data }) => setSelectedDiseases(data?.map(d => d.disease_id) || []));
      supabase.from('trial_investigators').select('*').eq('trial_id', trial.id).order('order_index').then(({ data }) => setInvestigators(data || []));
    }
  }, [trial]);

  const upd = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const updArm = (i, k, v) => setArms(p => p.map((a, idx) => idx === i ? { ...a, [k]: v } : a));
  const updInv = (i, k, v) => setInvestigators(p => p.map((a, idx) => idx === i ? { ...a, [k]: v } : a));

  const [fetchingCriteria, setFetchingCriteria] = useState(false);
  const [fetchMsg, setFetchMsg] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [extractWarnings, setExtractWarnings] = useState([]);
  const [criteriaReviewed, setCriteriaReviewed] = useState(false);
  const [needsReview, setNeedsReview] = useState(() => isNew && ls_get(DRAFT_CRITERIA_KEY, []).some(c => c.source === 'protocol_ai'));

  async function extractFromProtocolPdf(file) {
    if (!file) return;
    if (file.type !== 'application/pdf') { setFetchMsg('Hata: Yalnızca PDF yükleyebilirsiniz.'); return; }
    if (file.size > 3 * 1024 * 1024) { setFetchMsg('Hata: PDF en fazla 3 MB olabilir. Protokolün yalnızca kriterler bölümünü yükleyin.'); return; }
    if (criteria.length > 0 && !window.confirm('Mevcut kriterler protokolden çıkarılanlarla değiştirilsin mi?')) return;
    setExtracting(true);
    setExtractWarnings([]);
    setFetchMsg('Protokol bölümü analiz ediliyor, bu işlem bir dakika sürebilir...');
    try {
      const pdfBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1]);
        reader.onerror = () => reject(new Error('Dosya okunamadı'));
        reader.readAsDataURL(file);
      });
      const r = await apiFetch('/api/extract-criteria', { method: 'POST', body: { trialId: trial?.id, pdfBase64 } });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Analiz hatası');
      setCriteria(d.criteria.map((c, i) => ({
        type: c.type, description: c.summary, category: c.category, parameter: c.parameter,
        operator: c.operator, value: c.value, unit: c.unit, is_key: c.is_key, source: 'protocol_ai', order_index: i,
      })));
      setExtractWarnings(d.warnings || []);
      setCriteriaReviewed(false);
      setNeedsReview(true);
      const inc = d.criteria.filter(c => c.type === 'inclusion').length;
      setFetchMsg(`Tamamlandı: ${inc} dahil, ${d.criteria.length - inc} hariç kriter özetlendi. Kaydetmeden önce protokolle karşılaştırın.`);
    } catch (e) {
      setFetchMsg('Hata: ' + e.message);
    }
    setExtracting(false);
  }

  async function fetchAndTranslateCriteria() {
    if (!form.nct_number.trim()) return;
    setFetchingCriteria(true);
    setFetchMsg("ClinicalTrials.gov'dan kriterler aliniyor...");
    try {
      const nct = form.nct_number.trim();

      // 1. NCT kriterlerini cek
      const nctRes = await apiFetch('/api/nct?nct=' + encodeURIComponent(nct));
      if (!nctRes.ok) {
        const errData = await nctRes.json();
        throw new Error(errData.error || 'NCT bulunamadi');
      }
      const nctData = await nctRes.json();
      const rawCriteria = nctData.criteria || '';
      if (!rawCriteria) throw new Error('Kriter bilgisi bulunamadi');

      // 2. Turkcestir
      setFetchMsg("Kriterler Turkcestiriliyor...");
      const transRes = await apiFetch('/api/translate', { method: 'POST', body: { criteria: rawCriteria } });
      if (!transRes.ok) {
        const errData = await transRes.json();
        throw new Error(errData.error || 'Ceviri hatasi');
      }
      const transData = await transRes.json();
      const inclusion = transData.inclusion || [];
      const exclusion = transData.exclusion || [];

      if (inclusion.length === 0 && exclusion.length === 0) throw new Error('Kriter ayristirilamadi');

      const newCriteria = [
        ...inclusion.map((desc, i) => ({ type: 'inclusion', description: desc, order_index: i, source: 'nct' })),
        ...exclusion.map((desc, i) => ({ type: 'exclusion', description: desc, order_index: i, source: 'nct' })),
      ];
      setCriteria(newCriteria);
      setFetchMsg("Tamamlandi: " + inclusion.length + " dahil, " + exclusion.length + " haric kriter aktarildi.");
      setTimeout(() => setFetchMsg(''), 5000);
    } catch(e) {
      setFetchMsg("Hata: " + e.message);
      setTimeout(() => setFetchMsg(''), 5000);
    }
    setFetchingCriteria(false);
  }
  async function addBranch() {
    if (!newBranchName.trim()) return;
    const { data } = await supabase.from('branches').insert([{ name: newBranchName.trim() }]).select().single();
    if (data) {
      upd('branch_id', String(data.id));
      if (onRefresh) await onRefresh(true);
    }
    setShowAddBranch(false);
    setNewBranchName('');
  }

  async function addDisease() {
    if (!newDiseaseName.trim()) return;
    const code = newDiseaseCode.trim().toUpperCase() || newDiseaseName.trim().substring(0,3).toUpperCase();
    const { data } = await supabase.from('diseases').insert([{ name: newDiseaseName.trim(), code }]).select().single();
    if (data) {
      setSelectedDiseases(prev => [...prev, data.id]);
      if (onRefresh) await onRefresh(true);
    }
    setShowAddDisease(false);
    setNewDiseaseName('');
    setNewDiseaseCode('');
  }

  function addCriterion() {
    if (!newC.description.trim()) return;
    setCriteria(p => [...p, { ...newC, order_index: p.length }]);
    setNewC(p => ({ ...p, description: '' }));
  }

  async function handleSave() {
    if (!form.title.trim()) return alert('Çalışma adı zorunludur.');
    if (needsReview && !criteriaReviewed) return alert('Protokolden çıkarılan kriterleri protokolle karşılaştırdığınızı onaylamadan kaydedemezsiniz.');
    setSaving(true);
    try {
      let tid = trial?.id;

      const cleanForm = {
        ...form,
        branch_id: form.branch_id ? parseInt(form.branch_id) : null,
        start_date: form.start_date === '' ? null : form.start_date,
        estimated_end_date: form.estimated_end_date === '' ? null : form.estimated_end_date,
      };
      // disease_id eski alan - kaldır
      delete cleanForm.disease_id;

      if (trial?.id) {
        const { error: updateErr } = await supabase.from('trials')
          .update({ ...cleanForm, updated_at: new Date().toISOString() })
          .eq('id', trial.id);
        if (updateErr) throw new Error('Trial güncelleme hatası: ' + updateErr.message);
      } else {
        const { data, error: insertErr } = await supabase.from('trials')
          .insert([{ ...cleanForm, created_by: currentUserId }])
          .select().single();
        if (insertErr) throw new Error('Trial kayıt hatası: ' + insertErr.message);
        tid = data?.id;
      }

      if (!tid) throw new Error('Trial ID alınamadı');

      // Kriterler
      await supabase.from('criteria').delete().eq('trial_id', tid);
      if (criteria.length > 0) {
        const { error: critErr } = await supabase.from('criteria').insert(
          criteria.map((c, i) => ({
            trial_id: tid, type: c.type, description: c.description, order_index: i,
            category: c.category || null, parameter: c.parameter || null, operator: c.operator || null,
            value: c.value || null, unit: c.unit || null, is_key: !!c.is_key, source: c.source || 'manual',
          }))
        );
        if (critErr) throw new Error('Kriter kayıt hatası: ' + critErr.message);
      }
      if (needsReview && criteriaReviewed) {
        const { error: revErr } = await supabase.from('trials')
          .update({ criteria_reviewed_by: currentUserId, criteria_reviewed_at: new Date().toISOString() }).eq('id', tid);
        if (revErr) throw new Error('Kriter onayı kaydedilemedi: ' + revErr.message);
      }

      // Kollar
      await supabase.from('trial_arms').delete().eq('trial_id', tid);
      const validArms = arms.filter(a => a.name.trim() && a.agents.trim());
      if (validArms.length > 0) {
        const { error: armErr } = await supabase.from('trial_arms').insert(
          validArms.map((a, i) => ({ trial_id: tid, name: a.name, agents: a.agents, description: a.description || '', order_index: i }))
        );
        if (armErr) throw new Error('Kol kayıt hatası: ' + armErr.message);
      }

      // Araştırmacılar
      await supabase.from('trial_investigators').delete().eq('trial_id', tid);
      const validInvs = investigators.filter(inv => inv.center_name.trim());
      if (validInvs.length > 0) {
        const { error: invErr } = await supabase.from('trial_investigators').insert(
          validInvs.map((inv, i) => ({ trial_id: tid, center_name: inv.center_name, city: inv.city || '', investigator: inv.investigator || '', phone: inv.phone || '', email: inv.email || '', order_index: i }))
        );
        if (invErr) throw new Error('Araştırmacı kayıt hatası: ' + invErr.message);
      }

      // Hastalıklar
      await supabase.from('trial_diseases').delete().eq('trial_id', tid);
      if (selectedDiseases.length > 0) {
        const { error: disErr } = await supabase.from('trial_diseases').insert(
          selectedDiseases.map(did => ({ trial_id: tid, disease_id: parseInt(did) }))
        );
        if (disErr) throw new Error('Hastalık kayıt hatası: ' + disErr.message);
      }

      ls_del(DRAFT_KEY, DRAFT_CRITERIA_KEY, DRAFT_ARMS_KEY, DRAFT_INVESTIGATORS_KEY, 'tt_trial_diseases');
      
      setSaving(false);

      // Bildirim gönder - onSave'den önce tamamla
      if (sendNotification && tid) {
        try {
          const nr = await apiFetch('/api/notify-trial', { method: 'POST', body: { trialTitle: form.title, trialId: tid } });
          const nd = await nr.json();
          if (nd.sent) alert(nd.sent + ' kullanıcıya bildirim e-postası gönderildi.');
        } catch(e) {
          console.error('Bildirim hatası:', e);
        }
      }

      onSave();
    } catch(e) {
      alert('Kayıt hatası: ' + e.message);
      setSaving(false);
    }
  }

  function handleCancel() {
    if (isNew && hasDraft() && window.confirm('Taslak silinsin mi?')) ls_del(DRAFT_KEY, DRAFT_CRITERIA_KEY, DRAFT_ARMS_KEY, DRAFT_INVESTIGATORS_KEY, 'tt_trial_diseases');
    onCancel();
  }

  return (
    <div style={S.formCard}>
      <h3 style={S.formTitle}>
        {trial ? 'Çalışmayı Düzenle' : 'Yeni Çalışma Ekle'}
        {isNew && <span style={{ fontSize: '0.75rem', color: '#10b981', marginLeft: '0.75rem' }}>💾 Otomatik kaydediliyor</span>}
      </h3>

      {/* Temel Bilgiler */}
      <div style={S.grid}>
        <div style={{ ...S.fg, gridColumn: '1 / -1' }}>
          <label style={S.label}>Çalışma Adı *</label>
          <textarea style={{ ...S.input, height: '70px', resize: 'vertical' }} value={form.title} onChange={e => upd('title', e.target.value)} placeholder="Çalışmanın tam adı" />
        </div>
        <div style={S.fg}><label style={S.label}>Kısa Kod</label><input style={S.input} value={form.short_code} onChange={e => upd('short_code', e.target.value)} placeholder="Örn: CARTITUDE-4" /></div>
        <div style={S.fg}>
          <label style={S.label}>Branş</label>
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <select style={{ ...S.input, flex: 1 }} value={form.branch_id} onChange={e => upd('branch_id', e.target.value)}>
              <option value="">Branş seçiniz</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <button type="button" style={S.addInlineBtn} onClick={() => setShowAddBranch(true)} title="Yeni branş ekle">+</button>
          </div>
          {showAddBranch && (
            <div style={S.inlineAddBox}>
              <input style={{ ...S.input, flex: 1 }} placeholder="Branş adı" value={newBranchName} onChange={e => setNewBranchName(e.target.value)} onKeyDown={e => e.key === 'Enter' && addBranch()} autoFocus />
              <button type="button" style={S.inlineAddSave} onClick={addBranch}>Ekle</button>
              <button type="button" style={S.inlineAddCancel} onClick={() => { setShowAddBranch(false); setNewBranchName(''); }}>İptal</button>
            </div>
          )}
        </div>
        <div style={S.fg}>
          <label style={S.label}>Hastalık</label>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.4rem' }}>
            <button type="button" style={{ ...S.addInlineBtn, width: 'auto', padding: '0.3rem 0.75rem', fontSize: '0.85rem' }} onClick={() => setShowAddDisease(true)}>+ Yeni Hastalık Ekle</button>
          </div>
          <div style={S.diseaseCheckGrid}>
            {diseases.map(d => (
              <label key={d.id} style={S.chkLabel}>
                <input type="checkbox"
                  checked={selectedDiseases.includes(d.id)}
                  onChange={() => setSelectedDiseases(prev => prev.includes(d.id) ? prev.filter(x => x !== d.id) : [...prev, d.id])}
                />
                {' '}<span style={{ fontWeight: selectedDiseases.includes(d.id) ? '700' : '400' }}>{d.code} — {d.name}</span>
              </label>
            ))}
          </div>
          {showAddDisease && (
            <div style={S.diseaseAddBox}>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <input style={{ ...S.input, width: '110px', flexShrink: 0 }} placeholder="Kısaltma (örn: NHL)" value={newDiseaseCode} onChange={e => setNewDiseaseCode(e.target.value)} autoFocus />
                <input style={{ ...S.input, flex: 1 }} placeholder="Hastalık adı (örn: Non-Hodgkin Lenfoma)" value={newDiseaseName} onChange={e => setNewDiseaseName(e.target.value)} onKeyDown={e => e.key === 'Enter' && addDisease()} />
              </div>
              <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.4rem' }}>
                <button type="button" style={{ ...S.inlineAddSave, flex: 1 }} onClick={addDisease}>✅ Ekle</button>
                <button type="button" style={{ ...S.inlineAddCancel, flex: 1 }} onClick={() => { setShowAddDisease(false); setNewDiseaseName(''); setNewDiseaseCode(''); }}>İptal</button>
              </div>
            </div>
          )}
        </div>
        <div style={S.fg}><label style={S.label}>Faz</label>
          <select style={S.input} value={form.phase} onChange={e => upd('phase', e.target.value)}>
            <option value="">Seçin</option>
            {['I','II','III','IV','Genişletilmiş Erişim'].map(p => <option key={p} value={p}>Faz {p}</option>)}
          </select>
        </div>

        <div style={S.fg}><label style={S.label}>NCT Numarası</label><input style={S.input} value={form.nct_number} onChange={e => upd('nct_number', e.target.value)} placeholder="Örn: NCT04557098" /></div>
        <div style={S.fg}><label style={S.label}>Durum</label>
          <select style={S.input} value={form.status} onChange={e => upd('status', e.target.value)}>
            <option value="open">🟢 Açık</option>
            <option value="suspended">🟡 Askıda</option>
            <option value="closed">🔴 Kapalı</option>
          </select>
        </div>
        <div style={S.fg}><label style={S.label}>Başlangıç Tarihi</label><input style={S.input} type="date" value={form.start_date} onChange={e => upd('start_date', e.target.value)} /></div>
        <div style={S.fg}><label style={S.label}>Tahmini Bitiş</label><input style={S.input} type="date" value={form.estimated_end_date} onChange={e => upd('estimated_end_date', e.target.value)} /></div>
      </div>
      <div style={{ marginBottom: '1rem' }}>
        <label style={S.chkLabel}><input type="checkbox" checked={form.is_randomized} onChange={e => upd('is_randomized', e.target.checked)} />{' '}Randomize çalışma</label>
      </div>
      <div style={S.fg}><label style={S.label}>Notlar</label><textarea style={{ ...S.input, height: '80px', resize: 'vertical' }} value={form.notes} onChange={e => upd('notes', e.target.value)} placeholder="Ek bilgiler..." /></div>

      {/* ─── ÇALIŞMA KOLLARI ─── */}
      <div style={S.blockSection}>
        <div style={S.blockHeader}>
          <div>
            <span style={S.blockTitle}>💊 Çalışma Kolları</span>
            <span style={S.blockSub}> — Tek kol varsa sadece bir kol ekleyin</span>
          </div>
          <button type="button" style={S.addBlockBtn} onClick={() => setArms(p => [...p, { ...EMPTY_ARM, order_index: p.length }])}>+ Kol Ekle</button>
        </div>
        {arms.length === 0 && <div style={S.blockEmpty}>Henüz kol eklenmedi.</div>}
        {arms.map((arm, i) => (
          <div key={i} style={S.armCard}>
            <div style={S.blockRowHeader}>
              <span style={S.armBadge}>Kol {i + 1}</span>
              <button type="button" style={S.removeBlockBtn} onClick={() => setArms(p => p.filter((_, idx) => idx !== i))}>✕ Sil</button>
            </div>
            <div style={S.armGrid}>
              <div style={S.fg}><label style={S.label}>Kol Adı *</label><input style={S.input} value={arm.name} onChange={e => updArm(i, 'name', e.target.value)} placeholder="Örn: Kol A, Tedavi Kolu" /></div>
              <div style={{ ...S.fg, gridColumn: '1 / -1' }}><label style={S.label}>İlaç / Ajanlar *</label><input style={S.input} value={arm.agents} onChange={e => updArm(i, 'agents', e.target.value)} placeholder="Örn: Daratumumab + Lenalidomid + Deksametazon" /></div>
              <div style={{ ...S.fg, gridColumn: '1 / -1' }}><label style={S.label}>Doz / Rejim</label><textarea style={{ ...S.input, height: '60px', resize: 'vertical' }} value={arm.description} onChange={e => updArm(i, 'description', e.target.value)} placeholder="Doz, uygulama sıklığı, siklüs bilgisi (isteğe bağlı)" /></div>
            </div>
          </div>
        ))}
      </div>

      {/* ─── MERKEZLER VE ARAŞTIRMACILA ─── */}
      <div style={S.blockSection}>
        <div style={S.blockHeader}>
          <div>
            <span style={S.blockTitle}>🏥 Merkezler ve Araştırmacılar</span>
            <span style={S.blockSub}> — Her merkez için ayrı satır ekleyin</span>
          </div>
          <button type="button" style={{ ...S.addBlockBtn, background: '#0f766e' }} onClick={() => setInvestigators(p => [...p, { ...EMPTY_INVESTIGATOR, order_index: p.length }])}>+ Merkez Ekle</button>
        </div>
        {investigators.length === 0 && <div style={S.blockEmpty}>Henüz merkez eklenmedi.</div>}
        {investigators.map((inv, i) => (
          <div key={i} style={S.invCard}>
            <div style={S.blockRowHeader}>
              <span style={S.invBadge}>Merkez {i + 1}</span>
              <button type="button" style={S.removeBlockBtn} onClick={() => setInvestigators(p => p.filter((_, idx) => idx !== i))}>✕ Sil</button>
            </div>
            <div style={S.invGrid}>
              <div style={S.fg}><label style={S.label}>Merkez Adı *</label><input style={S.input} value={inv.center_name} onChange={e => updInv(i, 'center_name', e.target.value)} placeholder="Hastane / Üniversite adı" /></div>
              <div style={S.fg}><label style={S.label}>Şehir</label><input style={S.input} value={inv.city} onChange={e => updInv(i, 'city', e.target.value)} placeholder="İstanbul" /></div>
              <div style={S.fg}><label style={S.label}>Araştırmacı</label><input style={S.input} value={inv.investigator} onChange={e => updInv(i, 'investigator', e.target.value)} placeholder="Dr. ..." /></div>
              <div style={S.fg}><label style={S.label}>Telefon</label><input style={S.input} value={inv.phone} onChange={e => updInv(i, 'phone', e.target.value)} placeholder="05321234567" maxLength={11} /></div>
              <div style={{ ...S.fg, gridColumn: '1 / -1' }}><label style={S.label}>E-posta</label><input style={S.input} type="email" value={inv.email} onChange={e => updInv(i, 'email', e.target.value)} placeholder="arastirmaci@hastane.edu.tr" /></div>
            </div>
          </div>
        ))}
      </div>

      {/* ─── DAHİL ETME KRİTERLERİ ─── */}
      <div style={S.blockSection}>
        <div style={S.blockHeader}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span style={S.blockTitle}>✅ Dahil Etme Kriterleri</span>
            <button
              type="button"
              style={{ background: '#1d4ed8', color: 'white', border: 'none', padding: '0.4rem 0.9rem', borderRadius: '8px', cursor: form.nct_number.trim() ? 'pointer' : 'not-allowed', fontSize: '0.8rem', fontWeight: '600', opacity: form.nct_number.trim() ? 1 : 0.4 }}
              onClick={fetchAndTranslateCriteria}
              disabled={fetchingCriteria || !form.nct_number.trim()}
              title="NCT numarasi girilmisse kriterleri otomatik getir"
            >
              {fetchingCriteria ? '⏳' : '🤖 NCT Kriterlerini Cek'}
            </button>
            <label style={{ background: '#7c3aed', color: 'white', padding: '0.4rem 0.9rem', borderRadius: '8px', cursor: extracting ? 'wait' : 'pointer', fontSize: '0.8rem', fontWeight: '600', opacity: extracting ? 0.6 : 1, marginLeft: '0.5rem' }}
              title="Protokolün yalnızca uygunluk kriterleri bölümünü PDF olarak yükleyin. Dosya saklanmaz.">
              {extracting ? '⏳ Analiz ediliyor' : '📄 Protokol PDF\'inden çıkar'}
              <input type="file" accept="application/pdf" style={{ display: 'none' }} disabled={extracting}
                onChange={e => { extractFromProtocolPdf(e.target.files[0]); e.target.value = ''; }} />
            </label>
          </div>
          <div style={S.privacyNote}>🔒 Protokol PDF'i yalnızca analiz için işlenir, sistemde saklanmaz. Kriterler kelimesi kelimesine değil, eşik değerleri korunarak özetlenir; kaydetmeden önce protokolle karşılaştırılıp onaylanması gerekir.</div>
          {extractWarnings.length > 0 && (
            <div style={S.warnBox}><strong>⚠️ Kontrol edilmesi gerekenler</strong><ul style={{ margin: '0.3rem 0 0', paddingLeft: '1.2rem' }}>{extractWarnings.map((w, i) => <li key={i}>{w}</li>)}</ul></div>
          )}
          {fetchMsg && <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '6px', padding: '0.5rem 0.75rem', marginBottom: '0.5rem', fontSize: '0.82rem', color: '#1d4ed8' }}>{fetchMsg}</div>}
          <span style={S.blockTitle} />
        </div>
        {criteria.filter(c => c.type === 'inclusion').length === 0 && <div style={S.blockEmpty}>Henüz dahil etme kriteri eklenmedi.</div>}
        {criteria.map((c, i) => c.type !== 'inclusion' ? null : (
          <div key={i} style={S.inclRow}>
            <span style={S.inclNum}>{criteria.filter((x,j) => x.type==='inclusion' && j<=i).length}</span>
            <CriterionText c={c} onToggleKey={() => setCriteria(p => p.map((x, j) => j === i ? { ...x, is_key: !x.is_key } : x))} />
            <button style={S.removeBtn} onClick={() => setCriteria(p => p.filter((_, j) => j !== i))}>✕</button>
          </div>
        ))}
        <div style={S.cAddRow}>
          <input style={{ ...S.input, flex: 1 }} placeholder="Dahil etme kriteri ekle..." value={newC.type === 'inclusion' ? newC.description : ''} onFocus={() => setNewC(p => ({ ...p, type: 'inclusion' }))} onChange={e => setNewC({ type: 'inclusion', description: e.target.value })} onKeyDown={e => e.key === 'Enter' && addCriterion()} />
          <button style={{ ...S.addCBtn, background: '#059669' }} onClick={() => { setNewC(p => ({ ...p, type: 'inclusion' })); addCriterion(); }}>+ Ekle</button>
        </div>
      </div>

      {/* ─── HARİÇ TUTMA KRİTERLERİ ─── */}
      <div style={{ ...S.blockSection, borderColor: '#fecaca' }}>
        <div style={S.blockHeader}>
          <span style={S.blockTitle}>❌ Hariç Tutma Kriterleri</span>
        </div>
        {criteria.filter(c => c.type === 'exclusion').length === 0 && <div style={{ ...S.blockEmpty, background: '#fff5f5' }}>Henüz hariç tutma kriteri eklenmedi.</div>}
        {criteria.map((c, i) => c.type !== 'exclusion' ? null : (
          <div key={i} style={S.exclRow}>
            <span style={S.exclNum}>{criteria.filter((x,j) => x.type==='exclusion' && j<=i).length}</span>
            <CriterionText c={c} onToggleKey={() => setCriteria(p => p.map((x, j) => j === i ? { ...x, is_key: !x.is_key } : x))} />
            <button style={S.removeBtn} onClick={() => setCriteria(p => p.filter((_, j) => j !== i))}>✕</button>
          </div>
        ))}
        <div style={S.cAddRow}>
          <input style={{ ...S.input, flex: 1 }} placeholder="Hariç tutma kriteri ekle..." value={newC.type === 'exclusion' ? newC.description : ''} onFocus={() => setNewC(p => ({ ...p, type: 'exclusion' }))} onChange={e => setNewC({ type: 'exclusion', description: e.target.value })} onKeyDown={e => e.key === 'Enter' && addCriterion()} />
          <button style={{ ...S.addCBtn, background: '#dc2626' }} onClick={() => { setNewC(p => ({ ...p, type: 'exclusion' })); addCriterion(); }}>+ Ekle</button>
        </div>
      </div>

      {needsReview && (
        <label style={S.reviewBox}>
          <input type="checkbox" checked={criteriaReviewed} onChange={e => setCriteriaReviewed(e.target.checked)} style={{ width: 18, height: 18, marginTop: 2 }} />
          <span>Yapay zekâ ile özetlenen kriterleri protokolün ilgili bölümüyle tek tek karşılaştırdım; eşik değerler ve kritik (⭐) işaretler doğrudur. Bu onay adım ve tarihimle kaydedilecek.</span>
        </label>
      )}

      <div style={S.actions}>
        <button style={S.cancelBtn} onClick={handleCancel}>İptal</button>
        <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <input type="checkbox" id="sendNotif" checked={sendNotification} onChange={e => setSendNotification(e.target.checked)} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
            <label htmlFor="sendNotif" style={{ cursor: 'pointer', fontSize: '0.9rem', color: '#1e3a5f', fontWeight: '500' }}>
              📧 Kayıt sonrası tüm kullanıcılara bildirim e-postası gönder
            </label>
          </div>
          {sendNotification && profile?.role === 'superadmin' && (
            <button type="button"
              style={{ background: 'none', border: '1px solid #93c5fd', color: '#1d4ed8', borderRadius: '6px', padding: '0.3rem 0.75rem', fontSize: '0.8rem', cursor: 'pointer' }}
              onClick={async () => {
                if (!form.title.trim()) { alert('Önce çalışma adını girin.'); return; }
                setNotifyMsg('Test maili gönderiliyor...');
                const r = await apiFetch('/api/notify-trial', { method: 'POST', body: { trialTitle: form.title, trialId: trial?.id || 'test', test: true } });
                const d = await r.json();
                setNotifyMsg(d.sent ? 'Test maili ' + d.to + ' adresine gönderildi ✅' : 'Hata: ' + d.error);
                setTimeout(() => setNotifyMsg(''), 5000);
              }}
            >
              🧪 Sadece bana test maili gönder
            </button>
          )}
        </div>
        {notifyMsg && <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '0.6rem 1rem', marginBottom: '0.75rem', fontSize: '0.85rem', color: '#059669' }}>{notifyMsg}</div>}
        <button style={S.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Kaydediliyor...' : '💾 Kaydet'}</button>
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  privacyNote: { fontSize: '0.75rem', color: '#6b21a8', background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: '6px', padding: '0.45rem 0.7rem', margin: '0.5rem 0' },
  warnBox: { fontSize: '0.8rem', color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '6px', padding: '0.5rem 0.75rem', marginBottom: '0.5rem' },
  reviewBox: { display: 'flex', gap: '0.6rem', alignItems: 'flex-start', background: '#faf5ff', border: '1px solid #c4b5fd', borderRadius: '8px', padding: '0.75rem 1rem', margin: '0 0 1rem', fontSize: '0.85rem', color: '#4c1d95', cursor: 'pointer' },
  requestBox: { background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '10px', padding: '1rem', marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.6rem', fontSize: '0.9rem', color: '#78350f' },
  requestRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', background: 'white', borderRadius: '8px', padding: '0.5rem 0.75rem' },
  warnTag: { marginLeft: '0.5rem', background: '#fee2e2', color: '#b91c1c', borderRadius: '6px', padding: '0.1rem 0.4rem', fontSize: '0.75rem' },
  container: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
  center: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', color: '#64748b' },
  pageHeader: { display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' },
  pageTitle: { fontSize: '1.5rem', color: '#1e3a5f', margin: 0 },
  myRoleBadge: { padding: '0.25rem 0.75rem', borderRadius: '999px', fontSize: '0.8rem', fontWeight: '600' },
  toast: { background: '#1e3a5f', color: 'white', padding: '0.75rem 1.5rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.9rem' },
  infoBox: { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.875rem', color: '#1e40af' },
  tabBar: { display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '2px solid #e2e8f0' },
  tabBtn: { padding: '0.6rem 1.25rem', border: 'none', borderRadius: '8px 8px 0 0', cursor: 'pointer', background: '#f1f5f9', color: '#64748b', fontWeight: '500', fontSize: '0.9rem' },
  tabActive: { background: '#1e3a5f', color: 'white' },
  secHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' },
  secTitle: { fontSize: '1.1rem', color: '#1e3a5f', margin: 0 },
  addBtn: { background: '#1e3a5f', color: 'white', border: 'none', padding: '0.6rem 1.25rem', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' },
  tableWrap: { overflowX: 'auto', borderRadius: '10px', border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: '#1e3a5f' },
  th: { padding: '0.75rem 1rem', textAlign: 'left', color: 'white', fontSize: '0.85rem', fontWeight: '600' },
  td: { padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#334155', borderBottom: '1px solid #e2e8f0' },
  code: { display: 'block', fontSize: '0.75rem', color: '#94a3b8' },
  dot: { color: 'white', padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600 },
  noEdit: { color: '#94a3b8', fontSize: '0.8rem' },
  roleSelect: { padding: '0.3rem 0.5rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.8rem', background: 'white', cursor: 'pointer' },
  editBtn: { background: '#dbeafe', color: '#1d4ed8', border: 'none', padding: '0.3rem 0.75rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', marginRight: '0.4rem' },
  delBtn: { background: '#fee2e2', color: '#dc2626', border: 'none', padding: '0.3rem 0.75rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', marginRight: '0.4rem' },
  approveBtn: { background: '#dcfce7', color: '#16a34a', border: 'none', padding: '0.3rem 0.75rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', marginRight: '0.4rem' },
  emptyCell: { textAlign: 'center', padding: '2rem', color: '#94a3b8' },
  formCard: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '1.5rem', marginBottom: '1.5rem' },
  formTitle: { fontSize: '1rem', color: '#1e3a5f', marginBottom: '1.25rem' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1rem' },
  fg: { marginBottom: '1rem' },
  label: { display: 'block', fontSize: '0.8rem', color: '#475569', fontWeight: '600', marginBottom: '0.35rem', textTransform: 'uppercase' },
  input: { width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box', background: 'white' },
  chkLabel: { display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.875rem', color: '#334155', cursor: 'pointer' },
  cRow: { display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.6rem 0.75rem', borderRadius: '6px', marginBottom: '0.4rem' },
  cAddRow: { display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' },
  addCBtn: { background: '#1e3a5f', color: 'white', border: 'none', padding: '0.6rem 1rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.875rem', whiteSpace: 'nowrap' },
  removeBtn: { background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '1rem', padding: '0', flexShrink: 0 },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1rem' },
  cancelBtn: { background: '#f1f5f9', color: '#475569', border: 'none', padding: '0.6rem 1.25rem', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' },
  saveBtn: { background: '#1e3a5f', color: 'white', border: 'none', padding: '0.6rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' },
  blockSection: { background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '1.25rem', marginBottom: '1rem' },
  blockHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' },
  blockTitle: { fontSize: '0.9rem', fontWeight: '700', color: '#1e3a5f' },
  blockSub: { fontSize: '0.75rem', color: '#94a3b8' },
  addBlockBtn: { background: '#0891b2', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '500', whiteSpace: 'nowrap' },
  blockEmpty: { background: '#f8fafc', border: '2px dashed #e2e8f0', borderRadius: '8px', padding: '1.25rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.875rem' },
  blockRowHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' },
  removeBlockBtn: { background: '#fee2e2', color: '#dc2626', border: 'none', padding: '0.3rem 0.75rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' },
  armCard: { background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '10px', padding: '1rem', marginBottom: '0.75rem' },
  armBadge: { background: '#0891b2', color: 'white', padding: '0.2rem 0.7rem', borderRadius: '999px', fontSize: '0.8rem', fontWeight: '700' },
  armGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' },
  addInlineBtn: { background: '#1e3a5f', color: 'white', border: 'none', width: '36px', height: '36px', borderRadius: '8px', cursor: 'pointer', fontSize: '1.2rem', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  inlineAddBox: { display: 'flex', gap: '0.4rem', marginTop: '0.5rem', flexWrap: 'wrap', alignItems: 'center' },
  inlineAddSave: { background: '#059669', color: 'white', border: 'none', padding: '0.4rem 0.75rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap' },
  inlineAddCancel: { background: '#f1f5f9', color: '#475569', border: 'none', padding: '0.4rem 0.75rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap' },
  diseaseAddBox: { marginTop: '0.5rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.75rem' },
  fetchBtn: { background: '#1d4ed8', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '600', whiteSpace: 'nowrap' },
  fetchMsg: { background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '0.6rem 1rem', marginBottom: '0.75rem', fontSize: '0.85rem', fontWeight: '500' },
  diseaseCheckGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.4rem', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.75rem', marginBottom: '0.4rem' },
  inclRow: { display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.6rem 0.75rem', borderRadius: '6px', marginBottom: '0.4rem', background: '#f0fdf4' },
  inclNum: { background: '#bbf7d0', color: '#166534', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: '700', flexShrink: 0 },
  exclRow: { display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.6rem 0.75rem', borderRadius: '6px', marginBottom: '0.4rem', background: '#fef2f2' },
  exclNum: { background: '#fee2e2', color: '#dc2626', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: '700', flexShrink: 0 },
  invCard: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '1rem', marginBottom: '0.75rem' },
  invBadge: { background: '#0f766e', color: 'white', padding: '0.2rem 0.7rem', borderRadius: '999px', fontSize: '0.8rem', fontWeight: '700' },
  invGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' },
};

const OP_LABELS = { '>=': '≥', '<=': '≤', '!=': '≠', present: 'var', absent: 'yok', between: 'aralık', other: '' };

function CriterionText({ c, onToggleKey }) {
  const structured = c.parameter && c.operator && c.operator !== 'other'
    ? [c.parameter, OP_LABELS[c.operator] ?? c.operator, c.value, c.unit].filter(Boolean).join(' ')
    : null;
  return (
    <span style={{ flex: 1, fontSize: '0.875rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
      <span>
        <button type="button" onClick={onToggleKey} title="Kritik ön tarama kriteri" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 0.3rem 0 0', fontSize: '0.95rem', opacity: c.is_key ? 1 : 0.25 }}>⭐</button>
        {c.description}
      </span>
      {(structured || c.source === 'protocol_ai') && (
        <span style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
          {structured && <span style={{ fontSize: '0.72rem', background: '#f1f5f9', color: '#334155', borderRadius: '4px', padding: '0.05rem 0.4rem' }}>{structured}</span>}
          {c.source === 'protocol_ai' && <span style={{ fontSize: '0.72rem', background: '#ede9fe', color: '#6d28d9', borderRadius: '4px', padding: '0.05rem 0.4rem' }}>protokolden özetlendi</span>}
        </span>
      )}
    </span>
  );
}
